import { Component, EventEmitter, Input, OnInit, Output, OnDestroy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators, AbstractControl, AsyncValidatorFn, ValidationErrors } from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIconModule } from '@angular/material/icon';
import { Subject, Observable, of } from 'rxjs';
import { debounceTime, switchMap, map, catchError, takeUntil, distinctUntilChanged } from 'rxjs/operators';
import { StateManagementService, BusinessDetails } from '../../services/state-management.service';
import { OnboardingApiService } from '../../services/onboarding-api.service';
import { AddressAutocompleteComponent, OnboardAddress } from '../../shared/address-autocomplete/address-autocomplete.component';
import { environment } from '../../../environments/environment';

export const ORG_TYPES = [
  { value: 'non_profit', label: '501(c)(3) Nonprofit' },
  { value: 'religious', label: 'Religious Organization' },
  { value: 'educational', label: 'Educational Institution' },
  { value: 'foundation', label: 'Private Foundation' },
  { value: 'company', label: 'Other Company' },
];

export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA','HI','ID','IL','IN','IA',
  'KS','KY','LA','ME','MD','MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC','SD','TN','TX','UT','VT',
  'VA','WA','WV','WI','WY','DC',
];

const DEFAULT_DISCLOSURE =
  'Your contribution is tax-deductible to the extent allowed by law. ' +
  'No goods or services were provided in exchange for this donation.';

@Component({
  selector: 'app-business-details',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatSnackBarModule,
    MatIconModule,
    AddressAutocompleteComponent,
  ],
  templateUrl: './business-details.component.html',
  styleUrl: './business-details.component.scss',
})
export class BusinessDetailsComponent implements OnInit, OnDestroy {
  @Input() organizationId!: string;
  @Output() stepComplete = new EventEmitter<BusinessDetails>();

  form!: FormGroup;
  loading = false;
  orgTypes = ORG_TYPES;
  usStates = US_STATES;
  subDomainChecking = false;
  subDomainAvailable: boolean | null = null;
  subDomainPreview = '';

  private destroy$ = new Subject<void>();

  constructor(
    private fb: FormBuilder,
    private api: OnboardingApiService,
    private stateService: StateManagementService,
    private snackBar: MatSnackBar,
  ) {}

  ngOnInit(): void {
    // Initialize empty form first
    this.form = this.fb.group({
      name:         ['', Validators.required],
      dba:          [''],
      orgType:      ['non_profit', Validators.required],
      ein:          ['', [Validators.required, Validators.pattern(/^\d{2}-?\d{7}$/)]],
      phone:        ['', Validators.required],
      website:      [''],
      referralCode: ['', [Validators.minLength(6), Validators.maxLength(10)]],
      subDomain:    ['', [Validators.required, Validators.minLength(3), Validators.maxLength(30),
                      Validators.pattern(/^[a-z0-9][a-z0-9-]*[a-z0-9]$|^[a-z0-9]{3,}$/)]],
      disclosure:   [DEFAULT_DISCLOSURE, Validators.required],
      addressLookup: [null, null],
      line1:        ['', Validators.required],
      line2:        [''],
      city:         ['', Validators.required],
      state:        ['', Validators.required],
      postal_code:  ['', [Validators.required, Validators.pattern(/^\d{5}(-\d{4})?$/)]],
      country:      ['US'],
    });

    // Subscribe to state updates and populate form when data arrives
    this.stateService.state$.pipe(takeUntil(this.destroy$)).subscribe(state => {
      if (state.businessDetails) {
        const d = state.businessDetails;
        this.form.patchValue({
          name: d.name || '',
          dba: d.dba || '',
          orgType: d.orgType || 'non_profit',
          ein: d.ein || '',
          phone: d.phone || '',
          website: d.website || '',
          referralCode: d.referralCode || '',
          subDomain: d.subDomain || '',
          disclosure: d.disclosure || DEFAULT_DISCLOSURE,
          addressLookup: this.buildAddressObject(d.address),
          line1: d.address?.line1 || '',
          line2: d.address?.line2 || '',
          city: d.address?.city || '',
          state: d.address?.state || '',
          postal_code: d.address?.postal_code || '',
        }, { emitEvent: false });
      } else if (state.partnerPrefill) {
        // Pre-fill from partner referral link (only when no saved business details yet)
        const pf = state.partnerPrefill;
        const patch: Record<string, string> = {};
        if (pf.orgName && !this.form.get('name')!.value) patch['name'] = pf.orgName;
        if (pf.phone && !this.form.get('phone')!.value) patch['phone'] = pf.phone;
        if (pf.referralCode && !this.form.get('referralCode')!.value) patch['referralCode'] = pf.referralCode;
        if (Object.keys(patch).length) {
          this.form.patchValue(patch, { emitEvent: true });
        }
      }
    });

    // Listen to address lookup changes and auto-populate address fields
    this.form.get('addressLookup')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(addr => {
        if (addr) {
          this.form.patchValue({
            line1: addr.line1 || '',
            line2: addr.line2 || '',
            city: addr.city || '',
            state: addr.state || '',
            postal_code: addr.postal_code || '',
          }, { emitEvent: false });
        }
      });

    // Auto-suggest subdomain from org name
    this.form.get('name')!.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(name => {
        const subDomainCtrl = this.form.get('subDomain')!;
        if (!subDomainCtrl.dirty || !subDomainCtrl.value) {
          const suggested = this.slugify(name || '');
          subDomainCtrl.setValue(suggested, { emitEvent: true });
        }
      });

    // Live availability check as user types
    this.form.get('subDomain')!.valueChanges.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$),
    ).subscribe(value => {
      const slug = this.slugify(value || '');
      const appDomain = environment.appDomain || 'chareety.com';
      this.subDomainPreview = slug ? `${slug}.${appDomain}` : '';
      if (slug.length >= 3) {
        this.checkAvailability(slug);
      } else {
        this.subDomainAvailable = null;
        this.subDomainChecking = false;
      }
    });

    // Initialize preview for existing value
    const currentSlug = this.form.get('subDomain')!.value;
    if (currentSlug) {
      const appDomain = environment.appDomain || 'chareety.com';
      this.subDomainPreview = `${currentSlug}.${appDomain}`;
      this.subDomainAvailable = true; // already saved = available
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private buildAddressObject(addr: any): OnboardAddress | null {
    if (!addr) return null;
    // Return address object if any field has data
    if (addr.line1 || addr.city || addr.state || addr.postal_code) {
      return {
        line1: addr.line1 || '',
        line2: addr.line2 || '',
        city: addr.city || '',
        state: addr.state || '',
        postal_code: addr.postal_code || '',
        country: addr.country || 'US',
      };
    }
    return null;
  }

  slugify(value: string): string {
    return value.toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 30);
  }

  private checkAvailability(slug: string): void {
    // Skip check if this is the current org's own saved subdomain
    const org = this.stateService.snapshot.organization;
    if (org?.SubDomain === slug) {
      this.subDomainAvailable = true;
      this.subDomainChecking = false;
      return;
    }

    this.subDomainChecking = true;
    this.subDomainAvailable = null;

    this.api.checkSubDomainAvailability(slug).subscribe({
      next: (result) => {
        this.subDomainAvailable = result.available;
        this.subDomainChecking = false;
        if (!result.available) {
          this.form.get('subDomain')!.setErrors({ unavailable: true });
        }
      },
      error: () => {
        this.subDomainChecking = false;
        // Fail open — don't block the user if check fails
        this.subDomainAvailable = null;
      },
    });
  }

  async onSubmit(): Promise<void> {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    if (this.subDomainAvailable === false) {
      this.snackBar.open('Please choose a different subdomain — that one is taken.', 'Close', { duration: 4000 });
      return;
    }
    this.loading = true;
    const v = this.form.value;

    const details: BusinessDetails = {
      name:       v.name,
      dba:        v.dba,
      orgType:    v.orgType,
      ein:        v.ein,
      phone:      v.phone,
      website:    v.website,
      disclosure: v.disclosure,
      subDomain:  this.slugify(v.subDomain),
      referralCode: v.referralCode ? v.referralCode.toUpperCase().trim() : undefined,
      address: {
        line1:       v.line1,
        line2:       v.line2,
        city:        v.city,
        state:       v.state,
        postal_code: v.postal_code,
        country:     v.country,
      },
    };

    try {
      await this.api.saveBusinessDetails(this.organizationId, details).toPromise();
      this.stateService.setBusinessDetails(details);
      this.snackBar.open('Business details saved!', 'Close', { duration: 3000 });
      this.stepComplete.emit(details);
    } catch (err: any) {
      this.snackBar.open('Error saving details: ' + (err.message || err), 'Close', { duration: 5000 });
    } finally {
      this.loading = false;
    }
  }
}
